// ── MonthlyRevenueInput.js — 월별 매출 입력 (한도 계산용) ─────────────────
// 사용자가 매출을 모르면 미상 처리. 의제매입공제 한도는 매출의 일정 비율이라 필요.

import React, { useState, useEffect } from 'react';
import { quarterMonths, quarterRevenueFromMonthly } from '../useTaxPeriod';

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('ko-KR');
const parseMoney = (s) => Number(String(s || '').replace(/[^\d]/g, '')) || 0;

// 손익관리 매출 합계 추출 (card + cash + delivery)
function plRevenueFor(profitLoss, month) {
  const r = profitLoss?.[month]?.revenue;
  if (!r) return 0;
  return (Number(r.card) || 0) + (Number(r.cash) || 0) + (Number(r.delivery) || 0);
}

export default function MonthlyRevenueInput({
  year, quarter,
  monthlyRevenue = {},
  profitLoss = {},   // 손익관리 매출 — 거래장부 매출 미입력 시 fallback
  onSave,            // async (monthMap) => void
  onClose,
}) {
  const months = quarterMonths(year, quarter);

  // 우선순위: profitLoss(손익관리)가 master — 항상 우선.
  // monthlyRevenue는 손익관리에 데이터 없는 월에만 fallback.
  // 사용자가 모달에서 직접 수정한 값은 그 세션에서만 유효 (저장 후엔 손익관리가 최신값으로 다시 우선).
  const initialFor = (m) => {
    const pl = plRevenueFor(profitLoss, m);
    if (pl > 0) return pl;
    if (monthlyRevenue?.[m] != null && monthlyRevenue[m] !== '') return monthlyRevenue[m];
    return '';
  };

  const [draft, setDraft] = useState(() => {
    const init = {};
    months.forEach(m => { init[m] = initialFor(m); });
    return init;
  });
  // 어떤 월이 손익관리에서 가져온 것인지 표시 (사용자에게 안내용)
  // 손익관리 값이 0보다 크면 그게 우선이므로 fromPL로 표시
  const [fromPL, setFromPL] = useState(() => {
    const set = new Set();
    months.forEach(m => {
      if (plRevenueFor(profitLoss, m) > 0) set.add(m);
    });
    return set;
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const init = {};
    const set = new Set();
    months.forEach(m => {
      init[m] = initialFor(m);
      if (plRevenueFor(profitLoss, m) > 0) set.add(m);
    });
    setDraft(init);
    setFromPL(set);
  }, [year, quarter]);  // eslint-disable-line

  const setMonth = (m, val) => {
    setDraft(d => ({ ...d, [m]: parseMoney(val) }));
    // 사용자가 직접 수정 → 손익관리 fallback 표시 해제
    setFromPL(prev => {
      if (!prev.has(m)) return prev;
      const next = new Set(prev);
      next.delete(m);
      return next;
    });
  };

  const total = months.reduce((s, m) => s + (parseMoney(draft[m])), 0);
  const annualEst = total > 0 ? total * 4 : 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      // 빈 문자열은 0으로 저장 안 하고, 명시적 입력만 저장 (미입력 = 미상)
      const next = { ...monthlyRevenue };
      months.forEach(m => {
        const v = parseMoney(draft[m]);
        if (v > 0) next[m] = v;
        else delete next[m];
      });
      await onSave(next);
      onClose && onClose();
    } catch (e) {
      console.error(e);
      alert('저장 실패: ' + (e.message || e));
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 14, width: 480, maxWidth: '100%',
        boxShadow: '0 8px 40px rgba(0,0,0,0.2)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid #eee',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#f9fafb',
        }}>
          <div>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>의제매입공제 한도 계산용</div>
            <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2 }}>월별 매출 합계 입력</div>
          </div>
          <button onClick={onClose} type="button"
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
        </div>

        <div style={{ padding: 18 }}>
          <div style={{
            padding: 10, marginBottom: 14, borderRadius: 7,
            background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 11, color: '#1e40af',
          }}>
            💡 매출은 카드 + 현금 + 배달앱 모두 합한 <strong>총 매출</strong> (부가세 포함).
            손익관리에 입력된 값이 있으면 자동으로 가져옵니다 (수정 가능).
          </div>

          {months.map(m => {
            const [y, mm] = m.split('-');
            const isFromPL = fromPL.has(m);
            return (
              <div key={m} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: '0 0 60px', fontSize: 13, fontWeight: 600, color: '#555' }}>
                    {parseInt(mm, 10)}월
                  </div>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input type="text" inputMode="numeric" placeholder="0"
                      value={draft[m] ? fmt(draft[m]) : ''}
                      onChange={e => setMonth(m, e.target.value)}
                      style={{
                        width: '100%', padding: '8px 30px 8px 10px',
                        border: '1px solid', borderColor: isFromPL ? '#FED7AA' : '#ddd',
                        background: isFromPL ? '#FAFAFA' : '#fff',
                        borderRadius: 7, fontSize: 14,
                        textAlign: 'right', boxSizing: 'border-box',
                      }} />
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#888' }}>원</span>
                  </div>
                </div>
                {isFromPL && (
                  <div style={{ fontSize: 10, color: '#EA580C', marginTop: 3, marginLeft: 70 }}>
                    📊 손익관리에서 자동 입력 — 수정하면 별도 저장됩니다
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ marginTop: 14, padding: 12, background: '#f9fafb', borderRadius: 7 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: '#666' }}>분기 매출 합계</span>
              <strong style={{ color: '#18181B' }}>{fmt(total)}원</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888' }}>
              <span>연 매출 추정 (× 4)</span>
              <span>{fmt(annualEst)}원</span>
            </div>
          </div>
        </div>

        <div style={{
          padding: '12px 18px', borderTop: '1px solid #eee',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          background: '#f9fafb',
        }}>
          <button onClick={onClose} type="button"
            style={{ padding: '8px 16px', border: '1px solid #ddd', background: '#fff', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
            취소
          </button>
          <button onClick={handleSave} type="button" disabled={saving}
            style={{
              padding: '8px 18px', border: 'none', background: '#FF6F0F',
              color: '#fff', borderRadius: 7, cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 700, opacity: saving ? 0.6 : 1,
            }}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
